import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, ClipboardCopy, FileDown, RefreshCw, UploadCloud } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { requireSupabase } from "../lib/supabase";
import { formatDateTime, loadWarehouseSnapshot, type EasyTecSnapshot } from "../lib/warehouseData";
import { goodsReceipts, warehouseName } from "../lib/demoData";
import { Button, EmptyState, ErrorBanner, LoadingState, PageHeader, Panel, StatusBadge } from "../components/Ui";
import { OrderLink } from "../components/OrderLink";

type DemoEasyTec = EasyTecSnapshot & { demo: true };

function demoRows(): DemoEasyTec[] {
  return goodsReceipts
    .filter((receipt) => receipt.easyTecStatus === "offen")
    .map((receipt) => ({
      id: receipt.id,
      status: "open",
      completedAt: null,
      receiptId: receipt.id,
      orderNumber: receipt.orderNumber,
      supplier: receipt.supplier,
      receivedAt: receipt.receivedAt,
      documentStoragePath: "",
      documentMimeType: "application/pdf",
      warehouse: receipt.warehouse,
      warehouseName: warehouseName(receipt.warehouse),
      demo: true
    }));
}

export function EasyTecPage() {
  const { cloudConfigured, profile } = useAuth();
  const [rows, setRows] = useState<Array<EasyTecSnapshot | DemoEasyTec>>([]);
  const [loading, setLoading] = useState(cloudConfigured);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!cloudConfigured) {
      setRows(demoRows());
      return;
    }
    setLoading(true);
    setError("");
    try {
      const snapshot = await loadWarehouseSnapshot();
      setRows(snapshot.easyTec);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "EasyTec-Liste konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [cloudConfigured]);

  useEffect(() => { void load(); }, [load]);

  const openRows = useMemo(() => rows.filter((row) => row.status === "open"), [rows]);
  const completedRows = useMemo(() => rows.filter((row) => row.status === "uploaded"), [rows]);
  const canComplete = profile?.role === "admin" || profile?.role === "dispatcher";

  async function downloadDocument(row: EasyTecSnapshot | DemoEasyTec) {
    if ("demo" in row) {
      setMessage("In der lokalen Vorschau ist kein echtes Dokument hinterlegt.");
      return;
    }
    setBusyId(row.id);
    setError("");
    try {
      const { data, error: signedUrlError } = await requireSupabase().storage
        .from("delivery-notes")
        .createSignedUrl(row.documentStoragePath, 60);
      if (signedUrlError || !data?.signedUrl) throw signedUrlError ?? new Error("Kein Download-Link erhalten.");
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
      setMessage("Der private Download-Link ist 60 Sekunden gültig.");
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Lieferschein konnte nicht geöffnet werden.");
    } finally {
      setBusyId(null);
    }
  }

  async function copyOrderNumber(row: EasyTecSnapshot | DemoEasyTec) {
    try {
      await navigator.clipboard.writeText(row.orderNumber);
      setMessage(`Auftragsnummer ${row.orderNumber} wurde kopiert.`);
    } catch {
      setError("Die Auftragsnummer konnte nicht in die Zwischenablage kopiert werden.");
    }
  }

  async function complete(row: EasyTecSnapshot | DemoEasyTec) {
    if ("demo" in row) {
      setRows((current) => current.filter((item) => item.id !== row.id));
      setMessage("Vorschau-Eintrag als erledigt markiert.");
      return;
    }
    if (!window.confirm(`Wurde der Lieferschein für ${row.orderNumber} vollständig in EasyTec hochgeladen?`)) return;
    setBusyId(row.id);
    setError("");
    try {
      const { error: rpcError } = await requireSupabase().rpc("complete_easytec_export", {
        target_export_id: row.id,
        target_note: "Im Web-Admin als in EasyTec hochgeladen bestätigt."
      });
      if (rpcError) throw rpcError;
      setMessage(`${row.orderNumber} ist als in EasyTec hochgeladen protokolliert.`);
      await load();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "EasyTec-Status konnte nicht gespeichert werden.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <PageHeader
        title="EasyTec-Uploads"
        description="Private Lieferscheine herunterladen, Auftragsnummer kopieren und den manuellen EasyTec-Upload revisionssicher bestätigen."
        action={<Button variant="secondary" onClick={() => void load()} disabled={loading}><RefreshCw size={16} /> Aktualisieren</Button>}
      />
      {error && <ErrorBanner message={error} />}
      {message && <div className="success-banner" role="status"><CheckCircle2 size={17} /><span>{message}</span></div>}
      <Panel title={`${openRows.length} Upload${openRows.length === 1 ? "" : "s"} offen`} description="EasyTec bleibt bewusst ein manueller Schritt, da keine API verfügbar ist.">
        {loading ? <LoadingState /> : openRows.length === 0 ? (
          <EmptyState title="Keine Uploads offen" description="Alle erfassten Lieferscheine sind aktuell abgearbeitet." />
        ) : (
          <div className="easytec-list">
            {openRows.map((row) => <article key={row.id}>
              <span className="easytec-list__icon"><UploadCloud size={20} /></span>
              <span><strong><OrderLink orderNumber={row.orderNumber} /></strong><small>{row.supplier} · Eingang {formatDateTime(row.receivedAt)}</small></span>
              <StatusBadge tone="warning">Upload offen</StatusBadge>
              <span className="easytec-list__actions">
                <Button variant="secondary" onClick={() => void downloadDocument(row)} disabled={busyId === row.id}><FileDown size={16} /> Lieferschein</Button>
                <Button variant="secondary" onClick={() => void copyOrderNumber(row)}><ClipboardCopy size={16} /> Nummer</Button>
                <Button onClick={() => void complete(row)} disabled={busyId === row.id || !canComplete}><CheckCircle2 size={16} /> Erledigt</Button>
              </span>
            </article>)}
          </div>
        )}
      </Panel>
      {completedRows.length > 0 && (
        <Panel title="Zuletzt erledigt" description="Diese Uploads wurden mit Benutzer und Zeitpunkt protokolliert.">
          <div className="easytec-list">
            {completedRows.slice(0, 10).map((row) => <article key={row.id}>
              <span className="easytec-list__icon"><CheckCircle2 size={20} /></span>
              <span><strong><OrderLink orderNumber={row.orderNumber} /></strong><small>{row.supplier} · erledigt {formatDateTime(row.completedAt)}</small></span>
              <StatusBadge tone="success">Hochgeladen</StatusBadge>
            </article>)}
          </div>
        </Panel>
      )}
    </>
  );
}
